import get from 'lodash/get'
import {
  FieldErrors,
  type FieldValues,
  type UseFormSetError,
  useFormContext,
} from 'react-hook-form'
import { useRouter } from 'next/router'
import type { FormValues } from '../../form/schema'
import uriUtils from '../../lib/uriUtils'
import { useAnalytics } from '../../context/Analytics'
import validateAddress from '../../utils/validateAddress'
import validatePhoneNumber from '../../utils/validatePhoneNumber'

export const getErrorKeys = (fieldValues: FieldValues, hasCreditProtection: boolean): string[] => {
  // Get a list of error keys in order of where they appear on the form
  let errorKeys = ['names.firstName']

  // If a user has entered a middle initial/name, we need to validate that field as well (in correct order)
  if (fieldValues.names?.middleInitial) {
    errorKeys.push('names.middleInitial')
  }

  errorKeys = errorKeys.concat([
    'names.lastName',
    'dateOfBirth',
    'socialSecurityNumber',
    'streetAddress',
    'unit',
  ])

  errorKeys = errorKeys.concat([
    'city',
    'state',
    'zip',
    'housing',
    'payment',
    'employmentStatus',
    'jobRole',
    'paymentMethod',
    'timeAtCurrentJob',
    'industry',
    'selfEmploymentDuration',
    'workType',
    'unemploymentDuration',
    'degreeType',
    'institution',
    'fieldOfStudy',
    'jobCount',
    'additionalIncomeSources',
    'totalAnnualIncome',
    'accounts',
    'totalAccountBalance',
    'cashAdvance',
    'bnplFrequency',
    'bnplPurchaseType',
    'bnplLatePayments',
    'recentApplications',
    'recentAccountsOpened',
    'creditRepair',
    'educationLevel',
    'educationFieldOfStudy',
    'emails.email',
    'emails.confirmEmail',
    'phoneNumber',
  ])

  if (hasCreditProtection) {
    errorKeys.push('creditProtection')
  }

  return errorKeys
}

export const getFieldValidationErrors = (
  errorKeys: string[],
  errors: FieldErrors<FieldValues>
): string[] =>
  errorKeys.reduce<string[]>((acc, field) => (get(errors, field) ? acc.concat(field) : acc), [])

const useFormValidation = () => {
  const { query } = useRouter()
  const { getInviteCode, isValidPCode } = uriUtils()

  const { getValues, setError } = useFormContext()
  const { track, events } = useAnalytics()
  const fieldValues = getValues()

  const isValidForm = async (): Promise<boolean> => {
    const formValues = getValues() as FormValues
    const reactHookFormSetError = setError as unknown as UseFormSetError<FormValues>

    const inviteCode = getInviteCode(query)
    const pCode = isValidPCode(inviteCode) ? inviteCode : undefined

    // Validate address
    if (!(await validateAddress(formValues, reactHookFormSetError, pCode))) {
      const invalidAddressData = {
        streetAddress: fieldValues.streetAddress,
        unit: fieldValues.unit,
        city: fieldValues.city,
        state: fieldValues.state,
        zip: fieldValues.zip,
      }

      track?.(events.ADDRESS_VALIDATION.InvalidAddressError, {
        invalidAddressData,
        ...(pCode ? { pCode } : {}),
      })

      return false
    }

    // Validate phone number
    if (!(await validatePhoneNumber(formValues, reactHookFormSetError))) {
      track?.(events.PHONE_NUMBER_VALIDATION.InvalidPhoneNumberError, {
        invalidPhoneNumberData: {
          phoneNumber: fieldValues.phoneNumber,
        },
      })

      return false
    }

    return true
  }

  return { isValidForm }
}

export default useFormValidation
